import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Car, CheckCircle2, ChevronRight, Clock, GraduationCap, HelpCircle, Lightbulb, Loader2, MapPin } from 'lucide-react';
import { cancelLesson, fetchExam, fetchHistory, fetchLessons, fetchProgress, fetchSlots } from '../api/candidate';
import { useAuthStore } from '../store/auth';
import { durationLabel, monitorInitials } from '../lib/format';
import { Photo } from '../components/Photos';
import dayjs from '../lib/dates';
import PayableInvoices from '../components/PayableInvoices';
import PendingSignatures from '../components/PendingSignatures';
import ExamDateRequestPanel from '../components/ExamDateRequestPanel';
import { SchoolMessagesLink } from '../components/SchoolMessagesLink';
import { CodeQuestion } from '../components/CodeQuestion';
import { Badge, Button, Card, ConfirmDialog, Eyebrow, cx } from '../components/charte';
import { SESSION_RATING_MEANING, sessionRatingLabel } from '../lib/sessionRating';

// Accueil de l'élève (refonte du 14/10/2026) : mêmes données et mêmes actions qu'avant, rangées par urgence
// — examen proche, documents à signer et factures, prochaine leçon, réservation, progression, derniers bilans.

const TIPS = [
  'Réservez vos leçons en un clic parmi les créneaux ouverts par votre auto-école : votre moniteur est prévenu automatiquement.',
  'Révisez régulièrement les vérifications intérieures et extérieures du véhicule : elles tombent souvent à l’examen.',
  'Espacez raisonnablement vos leçons pour bien assimiler : mieux vaut de la régularité que de longues sessions rapprochées.',
  'Un créneau vous convient ? Réservez-le tôt : les places partent vite en période d’examen.',
  'En cas d’imprévu, annulez votre leçon depuis l’app : le créneau se libère aussitôt pour un autre élève.',
];

/** « Aujourd'hui », « Demain » ou le jour en toutes lettres. */
function dayLabel(iso: string) {
  const d = dayjs(iso).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (d === 0) return 'Aujourd’hui';
  if (d === 1) return 'Demain';
  const label = dayjs(iso).format('dddd D MMMM');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function MonitorAvatar({ name, photo, className }: { name: string | null; photo?: string | null; className?: string }) {
  return (
    <span className={cx('relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-avatar text-xs font-semibold text-primary-700', className)}>
      {monitorInitials(name)}
      <Photo url={photo} />
    </span>
  );
}

export default function DashboardView({
  onReserve,
  onOpenMessages,
  showCodeQuestion = true,
}: {
  onReserve: () => void;
  onOpenMessages: () => void;
  /** « Avez-vous déjà le code ? » (J46) : pas une fois le permis obtenu. */
  showCodeQuestion?: boolean;
}) {
  const candidate = useAuthStore((s) => s.candidate);
  const queryClient = useQueryClient();

  const lessonsQuery = useQuery({ queryKey: ['candidate', 'lessons'], queryFn: fetchLessons });
  const slotsQuery = useQuery({ queryKey: ['candidate', 'slots'], queryFn: fetchSlots });
  const progressQuery = useQuery({ queryKey: ['candidate', 'progress'], queryFn: fetchProgress });
  const examQuery = useQuery({ queryKey: ['candidate', 'exam'], queryFn: fetchExam });
  const historyQuery = useQuery({ queryKey: ['candidate', 'history'], queryFn: fetchHistory });

  const [confirmCancel, setConfirmCancel] = useState(false);
  const cancelMutation = useMutation({
    mutationFn: (lessonId: string) => cancelLesson(lessonId),
    onSuccess: () => {
      setConfirmCancel(false);
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'lessons'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'slots'] });
    },
  });

  const lessons = lessonsQuery.data ?? [];
  // Prochaine leçon = première leçon non terminée (la liste inclut aussi les leçons passées récentes).
  const nowMs = Date.now();
  const nextLesson = lessons.find((l) => new Date(l.endsAt).getTime() >= nowMs) ?? null;
  const openSlots = slotsQuery.data?.length ?? 0;

  const [tipIndex, setTipIndex] = useState(0);
  const tip = TIPS[tipIndex % TIPS.length];
  const [showCancelInfo, setShowCancelInfo] = useState(false);

  const exam = examQuery.data ?? null;
  const examDays = exam ? dayjs(exam.date).startOf('day').diff(dayjs().startOf('day'), 'day') : null;
  const examSoon = exam && examDays !== null && examDays >= 0 && examDays <= 21;
  const history = historyQuery.data ?? [];
  const competencies = progressQuery.data?.competencies ?? [];

  return (
    <div className="grid gap-5 sm:gap-6">
      <div>
        <h1 className="text-title-md text-heading sm:text-title-lg">Bonjour {candidate?.firstName ?? ''}</h1>
        <p className="mt-1 text-sm text-secondary">Voici l’essentiel de votre formation à la conduite.</p>
      </div>

      {/* Question de l'inscription, tant que la fiche n'a pas la date du code. */}
      {showCodeQuestion && candidate && <CodeQuestion candidateId={candidate.id} onOpenMessages={onOpenMessages} />}

      {/* Examen dans les 21 jours : en tête, impossible à manquer. */}
      {examSoon && (
        <section className="relative overflow-hidden rounded-card bg-panel-indigo p-5 text-white shadow-card sm:p-6">
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/15 text-lg font-bold">
              {examDays === 0 ? 'J' : `J-${examDays}`}
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[1px] text-white/70">Votre examen approche</p>
              <p className="mt-0.5 text-base font-semibold">
                {dayLabel(exam.date)}
                {examDays === 0 && ' · c’est aujourd’hui !'}
              </p>
              {exam.center && (
                <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-white/80">
                  <MapPin size={14} aria-hidden className="shrink-0" />
                  {exam.center}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* À faire : documents à signer, factures à régler (rien n'est affiché s'il n'y a rien). */}
      <PendingSignatures />
      <PayableInvoices />

      <div className="grid gap-5 sm:gap-6 lg:grid-cols-3 lg:items-start">
        {/* Prochaine leçon */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between gap-3">
            <Eyebrow as="h2">Prochaine leçon</Eyebrow>
            {nextLesson && <Badge tone="ok">Confirmée</Badge>}
          </div>
          {nextLesson ? (
            <>
              <div className="mt-4 flex items-start gap-4">
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-surface-indigo text-primary">
                  <Car size={24} aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-title-sm text-heading">{dayLabel(nextLesson.startsAt)}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-secondary">
                    <Clock size={14} aria-hidden />
                    {dayjs(nextLesson.startsAt).format('HH:mm')} – {dayjs(nextLesson.endsAt).format('HH:mm')} ·{' '}
                    {durationLabel(nextLesson.startsAt, nextLesson.endsAt)}
                  </p>
                  <p className="mt-2 flex items-center gap-2 text-sm text-body">
                    {nextLesson.instructorName ? (
                      <>
                        <MonitorAvatar name={nextLesson.instructorName} photo={nextLesson.instructorPhotoUrl} className="h-6 w-6 text-[10px]" />
                        Avec {nextLesson.instructorName}
                      </>
                    ) : (
                      'Moniteur à confirmer'
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                {nextLesson.bookedOnline ? (
                  <Button size="sm" onClick={() => setConfirmCancel(true)}>
                    Annuler
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" icon={HelpCircle} aria-expanded={showCancelInfo} onClick={() => setShowCancelInfo((v) => !v)}>
                    Annuler cette leçon ?
                  </Button>
                )}
              </div>
              {!nextLesson.bookedOnline && showCancelInfo && (
                <div className="mt-2 rounded-xl bg-warn-soft p-3 text-xs leading-relaxed text-warn-fg">
                  <p>Vous souhaitez annuler ? Cette leçon a été planifiée par votre auto-école, veuillez la contacter pour tout empêchement.</p>
                  <SchoolMessagesLink onOpen={onOpenMessages} />
                </div>
              )}
            </>
          ) : (
            <div className="mt-4 flex items-center gap-3 text-sm text-secondary">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-surface-2 text-muted">
                <Car size={20} aria-hidden />
              </span>
              Aucune leçon à venir pour le moment.
            </div>
          )}
        </Card>

        <div className="grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-1">
          {/* Réserver */}
          <Card className="flex flex-col">
            <div className="flex items-center justify-between gap-3">
              <Eyebrow as="h2">Réserver</Eyebrow>
              <Badge tone={openSlots > 0 ? 'ok' : 'mute'}>
                {openSlots} dispo{openSlots > 1 ? 's' : ''}
              </Badge>
            </div>
            <p className="mt-3 text-sm text-secondary">
              {openSlots > 0 ? 'Des créneaux sont ouverts par votre auto-école.' : 'Aucun créneau ouvert pour le moment.'}
            </p>
            <Button variant="primary" icon={CalendarPlus} className="mt-4 w-full" onClick={onReserve}>
              Réserver une leçon
            </Button>
          </Card>

          {/* Mon examen */}
          <Card>
            <div className="flex items-center justify-between gap-3">
              <Eyebrow as="h2">Mon examen</Eyebrow>
              {exam && <Badge tone="ok">Programmé</Badge>}
            </div>
            {exam ? (
              <div className="mt-3 flex items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-panel-indigo text-sm font-bold text-white">
                  {examDays !== null && examDays >= 0 ? `J-${examDays}` : <GraduationCap size={20} aria-hidden />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-heading">{dayjs(exam.date).format('ddd D MMM')}</p>
                  <p className="truncate text-xs text-secondary">{exam.center ?? 'Centre à confirmer'}</p>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-3 text-sm text-secondary">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-surface-2 text-muted">
                  <GraduationCap size={20} aria-hidden />
                </span>
                Aucun examen programmé pour l’instant.
              </div>
            )}
            {!exam && examQuery.isSuccess && <ExamDateRequestPanel />}
          </Card>
        </div>
      </div>

      {/* Ma progression par compétence (depuis les bilans moniteurs) */}
      {competencies.length > 0 && (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div>
              <h2 className="text-title-sm text-heading">Ma progression par compétence</h2>
              <p className="mt-1 text-sm text-secondary">Évaluée par vos moniteurs à chaque leçon.</p>
            </div>
            {progressQuery.data?.readyForExam && (
              <Badge tone="ok">
                <CheckCircle2 size={13} aria-hidden />
                Prêt(e) pour l’examen
              </Badge>
            )}
          </div>
          <div className="mt-5 grid gap-x-10 gap-y-4 lg:grid-cols-2">
            {competencies.map((c) => (
              <div key={c.label}>
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <span className="text-sm text-body">{c.label}</span>
                  <span className="text-sm font-semibold text-heading">{c.percent} %</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-pill bg-surface-2">
                  <div className="h-full rounded-pill bg-bar-primary transition-all" style={{ width: `${c.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Mes dernières leçons (bilans moniteurs) */}
      <Card>
        <h2 className="text-title-sm text-heading">Mes dernières leçons</h2>
        <p className="mt-1 text-sm text-secondary">Avec la note de votre moniteur.</p>
        {historyQuery.isLoading ? (
          <p className="mt-4 flex items-center text-sm text-secondary">
            <Loader2 className="mr-2 animate-spin" size={16} /> Chargement…
          </p>
        ) : history.length === 0 ? (
          <p className="mt-4 text-sm text-secondary">
            Aucun bilan de leçon pour le moment. Vos moniteurs les rempliront au fil de votre formation.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100">
            {history.map((b) => (
              <li key={b.id} className="flex items-start gap-3 py-3.5 first:pt-0 last:pb-0">
                <MonitorAvatar name={b.instructorName} photo={b.instructorPhotoUrl} className="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-semibold text-heading">{dayjs(b.date).format('ddd D MMM · HH:mm')}</span>
                    {b.instructorName && <span className="text-xs text-secondary">{b.instructorName}</span>}
                    {b.rating != null && (
                      <span className="text-xs font-medium text-amber-500" role="img" aria-label={sessionRatingLabel(b.rating)} title={sessionRatingLabel(b.rating)}>
                        {'★'.repeat(b.rating)}
                        <span className="text-slate-200">{'★'.repeat(Math.max(0, 5 - b.rating))}</span>
                      </span>
                    )}
                  </div>
                  {b.summary && <p className="mt-0.5 text-sm text-body">« {b.summary} »</p>}
                </div>
                {b.readyForExam && <Badge tone="ok">Prêt(e)</Badge>}
              </li>
            ))}
          </ul>
        )}
        {history.some((b) => b.rating != null) && <p className="mt-3 text-xs text-muted">{SESSION_RATING_MEANING}</p>}
      </Card>

      {/* Conseil Permio */}
      <section className="flex flex-col gap-3 rounded-card border border-primary-100 bg-surface-indigo p-5 sm:flex-row sm:items-center sm:gap-4 sm:p-6">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-primary shadow-navbar">
          <Lightbulb size={20} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-heading">Conseil Permio</p>
          <p className="mt-0.5 text-sm leading-relaxed text-body">{tip}</p>
        </div>
        <Button size="sm" iconEnd={ChevronRight} className="self-start sm:self-center" onClick={() => setTipIndex((i) => i + 1)}>
          Autres conseils
        </Button>
      </section>

      <ConfirmDialog
        open={confirmCancel && !!nextLesson}
        title="Annuler cette leçon ?"
        confirmLabel="Annuler la leçon"
        cancelLabel="Garder la leçon"
        tone="danger"
        pending={cancelMutation.isPending}
        error={
          cancelMutation.isError ? (
            <>
              Annulation impossible. Réessayez ou contactez votre auto-école.
              <SchoolMessagesLink onOpen={onOpenMessages} />
            </>
          ) : undefined
        }
        onConfirm={() => nextLesson && cancelMutation.mutate(nextLesson.id)}
        onCancel={() => setConfirmCancel(false)}
      >
        {nextLesson && (
          <>
            {dayLabel(nextLesson.startsAt)} à {dayjs(nextLesson.startsAt).format('HH:mm')}. Le créneau sera libéré pour un autre élève.
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
